import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "email-mass-send-panel__loader"
}
const _hoisted_2 = {
  key: 1,
  class: "email-mass-send-panel__content"
}
const _hoisted_3 = { class: "email-mass-send-panel__preview" }
const _hoisted_4 = { class: "email-mass-send-panel__field" }
const _hoisted_5 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_6 = { class: "typographies-paragraph-medium-regular email-mass-send-panel__field-value" }
const _hoisted_7 = { class: "email-mass-send-panel__field" }
const _hoisted_8 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = { class: "email-mass-send-panel__bottom" }
const _hoisted_12 = {
  key: 0,
  class: "email-mass-send-panel__attachments"
}
const _hoisted_13 = { class: "email-mass-send-panel__footer" }

import { ref, computed, watch, nextTick, onMounted, inject } from 'vue';
import {
    getEmailSettings,
    getListEmailTemplates,
    getEmailTemplate,
    getEmailTemplateTags,
    fetchSystemEmailTemplate,
    sendBatchEmail,
} from 'api/emailEngine/emailEngineService';
import EmailAttachments from 'vcomponents/email/emailPanels/EmailAttachments.vue';
import ScheduleEmailDialog from 'vcomponents/email/emailPanels/ScheduleEmailDialog.vue';
import Feature from 'Services/Feature';
import { Translator, PushToast } from 'js/shims-vue';
import { LEGACY_TAG_TO_NEW } from 'Helpers/email/legacyTagMapping';

interface EmailTemplateOption {
    id: number | string;
    name: string;
    hasAi?: boolean;
}

interface EmailTag {
    key: string;
    name: string;
}

interface Attachment {
    name: string;
    size: number;
    extension: string;
    public_link: string;
}

interface Props {
    docIds: string[];
    linkedtype: string;
    isPanelMinimized: boolean;
}

const SYSTEM_TEMPLATE_ID = 'system:document';


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailMassSendPanel',
  props: {
    docIds: {},
    linkedtype: {},
    isPanelMinimized: { type: Boolean }
  },
  emits: ['close-panel', 'toggle-panel-state'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const compassPanelRef = ref<{ isMinimized: boolean } | null>(null);
const splitSendButtonRef = ref(null);
const showDropdown = ref(false);
const showScheduleEmailDialog = ref(false);
const scheduledDate = ref<string | null>(null);
const showPanel = ref(true);
const isLoading = ref(true);
const isLoadingTemplate = ref(false);
const isSending = ref(false);
const senderEmail = ref('');
const templateSubject = ref('');
const templateContent = ref('');
const selectedTemplateId = ref<number | string>(SYSTEM_TEMPLATE_ID);
const automaticallyAttachDocument = ref(false);
const templateAttachments = ref<Attachment[]>([]);
const templates = ref<EmailTemplateOption[]>([]);
const tags = ref<EmailTag[]>([]);

const translations: Record<string, string> = {
    title: $t.__('email.massSend.title'),
    from: $t.__('email.massSend.from'),
    subject: $t.__('email.massSend.subject'),
    templateLabel: $t.__('email.massSend.templateLabel'),
    autoAttachDocument: $t.__('email.massSend.autoAttachDocument'),
    cancel: $t.__('common.actions.cancel'),
    send: $t.__('common.actions.send'),
    scheduleTitle: $t.__('email.ScheduleEmailDialog.title'),
};

const panelClasses = computed<(string | Record<string, boolean>)[]>(() => {
    return ['email-mass-send-panel', 'email-panel', { minimized: props.isPanelMinimized }];
});

const hasTemplateAttachments = computed<boolean>(() => {
    return templateAttachments.value.length > 0;
});

const isFeatureScheduleEmailEnabled = computed<boolean>(() => {
    return Feature.isEnabled('email-engine-scheduled-emails');
});

const isScheduledSend = computed<boolean>(() => {
    return scheduledDate.value !== null;
});

const warningMessage = computed<string>(() => {
    const key = isScheduledSend.value
        ? 'email.massSend.warningScheduled'
        : 'email.massSend.warning';
    const base = $t.n__(key, null, props.docIds.length, { count: props.docIds.length });
    if (automaticallyAttachDocument.value) {
        return `${base}\n${translations.autoAttachDocument}`;
    }
    return base;
});

const replaceTagsWithSpan = (message: string): string => {
    const tagsByKey = new Map(tags.value.map((tag) => [tag.key, tag.name]));
    return message.replace(/%\{?[\w.]+\}?%/g, (match) => {
        const newKey = LEGACY_TAG_TO_NEW[match] ?? match;
        const label = tagsByKey.get(newKey) ?? tagsByKey.get(match) ?? match;
        return `<span class="custom-tags-and-fields custom-tags-and-fields__default typographies-paragraph-small-semibold">${label}</span>`;
    });
};

const formattedSubject = computed<string>(() => {
    return replaceTagsWithSpan(templateSubject.value);
});

const formattedContent = computed<string>(() => {
    return replaceTagsWithSpan(templateContent.value);
});

const loadEmailSettings = async (): Promise<void> => {
    const response = await getEmailSettings();
    const { connection, automatically_attach_document } = response.data;
    if (connection) {
        senderEmail.value = connection.email ?? '';
    }
    automaticallyAttachDocument.value = automatically_attach_document ?? false;
};

const loadTags = async (): Promise<void> => {
    const response = await getEmailTemplateTags();
    if (response.data) {
        tags.value = response.data;
    }
};

const loadSystemTemplate = async (): Promise<void> => {
    const { data } = (await fetchSystemEmailTemplate('document', {
        type: props.linkedtype,
    })) as { data: { subject?: string; content?: string } };
    if (data) {
        templateSubject.value = data.subject ?? '';
        templateContent.value = data.content ?? '';
    }
};

const loadTemplates = async (): Promise<void> => {
    const response = await getListEmailTemplates();
    const systemTemplate: EmailTemplateOption = {
        id: SYSTEM_TEMPLATE_ID,
        name: $t.__('email.massSend.systemTemplate'),
    };
    const customTemplates: EmailTemplateOption[] = (response.data ?? []).filter(
        (template: EmailTemplateOption) => !template.hasAi,
    );
    templates.value = [systemTemplate, ...customTemplates];
};

const loadCustomTemplateContent = async (templateId: number): Promise<void> => {
    const data = (await getEmailTemplate(templateId)) as {
        subject?: string;
        message?: string;
        attachments?: Attachment[];
    };
    if (data) {
        templateSubject.value = data.subject ?? '';
        templateContent.value = data.message ?? '';
        templateAttachments.value = data.attachments ?? [];
    }
};

const onTemplateChange = async (templateId: number | string): Promise<void> => {
    if (!templateId) {
        return;
    }
    isLoadingTemplate.value = true;
    templateAttachments.value = [];
    try {
        if (templateId === SYSTEM_TEMPLATE_ID) {
            await loadSystemTemplate();
        } else {
            await loadCustomTemplateContent(templateId as number);
        }
    } finally {
        isLoadingTemplate.value = false;
    }
};

const openScheduleEmail = (): void => {
    showDropdown.value = false;
    showScheduleEmailDialog.value = true;
};

const handleSendScheduledEmail = (date: string): void => {
    scheduledDate.value = date;
    showScheduleEmailDialog.value = false;
    handleSend();
};

const buildBatchPayload = (): Record<string, unknown> => {
    const isSystemTemplate = selectedTemplateId.value === SYSTEM_TEMPLATE_ID;
    const template = isSystemTemplate
        ? { type: 'system', id: 'document' }
        : { type: 'custom', id: selectedTemplateId.value };

    const payload: Record<string, unknown> = {
        template,
        related: props.docIds.map((docId) => ({
            type: props.linkedtype,
            id: Number(docId),
        })),
    };
    if (scheduledDate.value) {
        payload.scheduled_at = scheduledDate.value;
    }
    return payload;
};

const handleSend = async (): Promise<void> => {
    isSending.value = true;
    try {
        await sendBatchEmail(buildBatchPayload());
        const toastText = isScheduledSend.value
            ? $t.__('email.massSend.successScheduledToast')
            : $t.__('email.massSend.successToast');
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            icon: true,
            text: toastText,
        });
        emit('close-panel');
    } catch (error: unknown) {
        const status = (error as { status?: number })?.status;
        if (status !== 402) {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('common.labels.toast.error'),
            });
        }
    } finally {
        isSending.value = false;
        scheduledDate.value = null;
    }
};

watch(
    () => props.isPanelMinimized,
    (newValue: boolean) => {
        nextTick(() => {
            if (compassPanelRef.value) {
                compassPanelRef.value.isMinimized = newValue;
            }
        });
    },
);

onMounted(async (): Promise<void> => {
    try {
        await Promise.all([loadEmailSettings(), loadTemplates(), loadTags(), loadSystemTemplate()]);
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_CompassLoader = _resolveComponent("CompassLoader")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassPlainButton = _resolveComponent("CompassPlainButton")!
  const _component_CompassSplitButton = _resolveComponent("CompassSplitButton")!
  const _component_CompassDropdown = _resolveComponent("CompassDropdown")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassPanel = _resolveComponent("CompassPanel")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(panelClasses.value)
  }, [
    _createVNode(_component_CompassPanel, {
      ref_key: "compassPanelRef",
      ref: compassPanelRef,
      modelValue: showPanel.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showPanel).value = $event)),
      size: _ctx.CompassEnums.PanelSize.LARGE,
      title: translations.title,
      enableShrink: "",
      onToggled: _cache[6] || (_cache[6] = ($event: any) => (emit('toggle-panel-state'))),
      onClosed: _cache[7] || (_cache[7] = ($event: any) => (emit('close-panel')))
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_component_CompassPlainButton, {
            label: translations.cancel,
            variant: _ctx.CompassEnums.PlainButtonVariantType.NEUTRAL,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close-panel')))
          }, null, 8 /* PROPS */, ["label", "variant"]),
          (isFeatureScheduleEmailEnabled.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_CompassSplitButton, {
                  ref_key: "splitSendButtonRef",
                  ref: splitSendButtonRef,
                  label: translations.send,
                  loading: isSending.value,
                  disabled: isLoading.value,
                  onClickOnLabel: handleSend,
                  onClickOnArrow: _cache[2] || (_cache[2] = ($event: any) => (showDropdown.value = !showDropdown.value))
                }, null, 8 /* PROPS */, ["label", "loading", "disabled"]),
                _createVNode(_component_CompassDropdown, {
                  isVisible: showDropdown.value,
                  "onUpdate:isVisible": _cache[3] || (_cache[3] = ($event: any) => ((showDropdown).value = $event)),
                  parentRef: "splitSendButtonRef",
                  placement: "top-end",
                  dropdownClass: "email-mass-send-panel__schedule-dropdown",
                  value: "scheduleAction",
                  "onUpdate:modelValue": openScheduleEmail
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassDropdownOption, {
                      label: translations.scheduleTitle,
                      value: "scheduleAction",
                      icon: "calendar-reschedule",
                      isCheckable: false
                    }, null, 8 /* PROPS */, ["label"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["isVisible"]),
                _createVNode(ScheduleEmailDialog, {
                  visible: showScheduleEmailDialog.value,
                  onScheduled: handleSendScheduledEmail,
                  onClose: _cache[4] || (_cache[4] = ($event: any) => (showScheduleEmailDialog.value = false))
                }, null, 8 /* PROPS */, ["visible"])
              ], 64 /* STABLE_FRAGMENT */))
            : (_openBlock(), _createBlock(_component_CompassButton, {
                key: 1,
                label: translations.send,
                loading: isSending.value,
                disabled: isLoading.value,
                onClick: handleSend
              }, null, 8 /* PROPS */, ["label", "loading", "disabled"]))
        ])
      ]),
      default: _withCtx(() => [
        (isLoading.value)
          ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_component_CompassLoader)
            ], 512 /* NEED_PATCH */)), [
              [_vShow, !__props.isPanelMinimized]
            ])
          : _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(translations.from), 1 /* TEXT */),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(senderEmail.value), 1 /* TEXT */)
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(translations.subject), 1 /* TEXT */),
                  _createElementVNode("span", {
                    class: "typographies-paragraph-medium-regular email-mass-send-panel__field-value rich-text-style",
                    innerHTML: formattedSubject.value
                  }, null, 8 /* PROPS */, _hoisted_9)
                ]),
                _createElementVNode("div", {
                  class: "email-mass-send-panel__body typographies-paragraph-medium-regular rich-text-style",
                  innerHTML: formattedContent.value
                }, null, 8 /* PROPS */, _hoisted_10)
              ]),
              _createElementVNode("div", _hoisted_11, [
                (hasTemplateAttachments.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templateAttachments.value, (attachment, index) => {
                        return (_openBlock(), _createBlock(EmailAttachments, {
                          key: index,
                          attachment: attachment,
                          removeBtn: ""
                        }, null, 8 /* PROPS */, ["attachment"]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ]))
                  : _createCommentVNode("v-if", true),
                _createVNode(_component_CompassFormItem, {
                  label: translations.templateLabel,
                  orientation: _ctx.CompassEnums.FormItemOrientation.HORIZONTAL
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassSelect, {
                      modelValue: selectedTemplateId.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedTemplateId).value = $event)),
                      class: "email-mass-send-panel__template-select",
                      menuClass: "email-mass-send-panel__template-menu",
                      disabled: isLoadingTemplate.value,
                      onChange: onTemplateChange
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templates.value, (template) => {
                          return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                            key: template.id,
                            label: template.name,
                            value: template.id
                          }, null, 8 /* PROPS */, ["label", "value"]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue", "disabled"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["label", "orientation"]),
                _createVNode(_component_CompassAlert, {
                  type: _ctx.CompassEnums.AlertType.WARNING,
                  description: warningMessage.value,
                  showClose: false
                }, null, 8 /* PROPS */, ["type", "description"])
              ])
            ], 512 /* NEED_PATCH */)), [
              [_vShow, !__props.isPanelMinimized]
            ])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["modelValue", "size", "title"])
  ], 2 /* CLASS */))
}
}

})